import { API_BASE, getAccessToken } from "@/lib/api/session";

export type CoachChatMessage = {
  role: "user" | "assistant" | "system";
  content: string;
};

export type CoachHistoryMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  conversationId: string;
};

export async function sendCoachMessage(
  messages: CoachChatMessage[],
  conversationId?: string
): Promise<
  | {
      ok: true;
      content: string;
      safetyLevel: string;
      model: string;
      conversationId: string | null;
    }
  | {
      ok: false;
      content: string;
    }
> {
  try {
    const token = await getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/api/v1/coach/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        messages,
        conversationId,
      }),
    });

    if (response.status === 401) {
      return {
        ok: false,
        content: "Logga in för att prata med coachen och spara historik.",
      };
    }

    if (!response.ok) {
      return {
        ok: false,
        content:
          "Coachen är tillfälligt otillgänglig. Prova igen om en stund, eller gå till Idag.",
      };
    }

    const json = (await response.json()) as {
      message?: { content?: string };
      safety?: { level?: string };
      model?: string;
      conversationId?: string | null;
    };

    return {
      ok: true,
      content:
        json.message?.content ??
        "Jag är här. Vill du prova något litet under Idag?",
      safetyLevel: json.safety?.level ?? "normal",
      model: json.model ?? "unknown",
      conversationId: json.conversationId ?? null,
    };
  } catch {
    return {
      ok: false,
      content:
        "Kunde inte nå coach-API:t. Starta `pnpm --filter @andrum/api dev` eller kontrollera VITE_API_URL.",
    };
  }
}

export async function loadCoachHistory(): Promise<CoachHistoryMessage[]> {
  const token = await getAccessToken();
  if (!token) return [];

  try {
    const response = await fetch(`${API_BASE}/api/v1/coach/history`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) return [];
    const json = (await response.json()) as {
      messages?: CoachHistoryMessage[];
    };
    return (json.messages ?? []).filter(
      (m): m is CoachHistoryMessage =>
        m.role === "user" || m.role === "assistant"
    );
  } catch {
    return [];
  }
}

export async function deleteAccountRemote(): Promise<
  { ok: true } | { ok: false; message: string }
> {
  const token = await getAccessToken();
  if (!token) {
    return { ok: false, message: "Du behöver vara inloggad." };
  }
  try {
    const response = await fetch(`${API_BASE}/api/v1/me`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as {
        message?: string;
      };
      return {
        ok: false,
        message: json.message ?? "Kunde inte radera kontot.",
      };
    }
    return { ok: true };
  } catch {
    return {
      ok: false,
      message: "Kunde inte nå API:t för radering.",
    };
  }
}
